import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MoreVertical, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getProject, updateTask } from "@/lib/time-api";
import { TaskFormDialog } from "@/components/time/TaskFormDialog";
import { SettingsBreadcrumb } from "@/components/projects/SettingsBreadcrumb";
import { FormAssignments } from "@/components/projects/FormAssignments";
import type { Task } from "@/types/time";

interface TaskDetailProps {
  projectId: string;
  taskId: string;
  isAdmin: boolean;
}

export function TaskDetail({ projectId, taskId, isAdmin }: TaskDetailProps) {
  const { orgId, slug } = useParams<{ orgId: string; slug: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  const base = `/${orgId}/${slug}/settings/projects`;

  const { data, isLoading } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => getProject(projectId),
  });

  const project = data ?? null;
  const task: Task | undefined = project?.tasks?.find((t) => t.id === taskId);

  async function handleUpdate(updateData: { name: string }) {
    try {
      await updateTask(projectId, taskId, updateData);
      toast.success("Task updated");
      qc.invalidateQueries({ queryKey: ["project", projectId] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update task");
    }
  }

  async function handleArchive() {
    try {
      await updateTask(projectId, taskId, { isArchived: true });
      toast.success("Task archived");
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      navigate(`${base}/${projectId}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to archive task");
    }
    setArchiveOpen(false);
  }

  if (isLoading || !project) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading...
      </div>
    );
  }

  if (!task) {
    return (
      <div className="space-y-4">
        <SettingsBreadcrumb
          items={[
            { label: "Projects", href: base },
            { label: project.name, href: `${base}/${projectId}` },
            { label: "Task not found" },
          ]}
        />
        <p className="text-sm text-muted-foreground">
          This task may have been archived or deleted.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <SettingsBreadcrumb
          items={[
            { label: "Projects", href: base },
            { label: project.name, href: `${base}/${projectId}` },
            { label: task.name },
          ]}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap className="h-5 w-5 text-muted-foreground shrink-0" />
            <h2 className="text-lg font-semibold">{task.name}</h2>
          </div>

          {isAdmin && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                Edit
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => setArchiveOpen(true)}
                  >
                    Archive
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
      </div>

      <FormAssignments
        assignableType="task"
        assignableId={taskId}
        isAdmin={isAdmin}
      />

      <TaskFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        onSubmit={handleUpdate}
        task={task}
      />

      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive task?</AlertDialogTitle>
            <AlertDialogDescription>
              "{task.name}" will be removed from the task list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
